<template>
  <div class="fiu-grid flex column gap-10px align-start w-full">
    <div v-if="loading" class="flex gap-10px align-center">
      <LoaderItem size="18px" /> Loading template...
    </div>

    <template v-else-if="meta">
      <div v-if="!meta.blank && showLegend" class="legend flex gap-20px align-center font-size-14">
        <span v-if="meta.isPlayerTemplate" class="flex gap-5px align-center"><span class="swatch key"></span>Key: type or paste a {{ PLAYER_KEY_COLUMN }}</span>
        <span v-if="meta.isPlayerTemplate" class="flex gap-5px align-center"><span class="swatch linked"></span>Filled automatically from player data (you can overwrite)</span>
        <span v-if="meta.isPlayerTemplate" class="flex gap-5px align-center"><span class="swatch not-found"></span>Player not found</span>
        <span v-if="defaultTextXs.length" class="flex gap-5px align-center"><span class="swatch rule"></span>{{ defaultTextLabel }} filled from the template rules (you can overwrite)</span>
        <span class="flex gap-5px align-center"><span class="swatch error"></span>Error (hover the cell)</span>
      </div>
      <div v-if="!meta.blank && showLegend" class="font-size-14 text-left hint">
        Copy rows from Excel and paste them here (Ctrl+V), or type directly. Right-click for row options.
        Each cell is checked as soon as you leave it. Columns with a <span class="dropdown-text">▾</span> offer a searchable list of accepted values (double-click or start typing).
        <template v-if="meta.columns?.some(c => GRID_DATE_COLUMNS.includes(c.name))">Date cells can be typed ({{ DATE_EXAMPLE }}) or picked from a calendar (click the cell's calendar icon or press Alt+↓).</template>
        <template v-if="meta.isPlayerTemplate">
          Columns marked <span class="linked-text">blue</span> are filled from your imported player data as soon as a {{ PLAYER_KEY_COLUMN }} is entered.
        </template>
      </div>

      <div v-if="nonCellErrors.length" class="errors-box flex column gap-5px align-start font-size-14">
        <b>{{ nonCellErrors.length }} error{{ nonCellErrors.length > 1 ? 's' : '' }}:</b>
        <div v-for="(error, i) in nonCellErrors" :key="i">• {{ describeError(error) }}</div>
      </div>
      <div v-if="cellErrorCount" class="toggle-error flex gap-10px align-center font-size-14">
        <span>{{ cellErrorCount }} cell error{{ cellErrorCount > 1 ? 's' : '' }} marked in red. Hover a cell to see the error.</span>
        <button class="button transparent small" @click="focusError(-1)">Previous</button>
        <button class="button transparent small" @click="focusError(1)">Next</button>
      </div>

      <div
        ref="gridEl"
        class="grid-holder w-full"
        @mouseover="onGridMouseOver"
        @click="onGridClick"
        @mousemove="onGridMouseMove"
        @mouseleave="hideTooltip(); setArrowHot(null)"
        @mousedown="hideTooltip()"
        @scroll.capture="hideTooltip()"
      ></div>

      <Teleport to="body">
        <div
          v-if="tooltip"
          class="fiu-cell-tooltip"
          :class="[tooltip.kind, { below: tooltip.below }]"
          :style="{ left: tooltip.left + 'px', top: tooltip.top + 'px' }"
          role="tooltip"
        >
          <div v-if="tooltip.heading" class="fiu-cell-tooltip-heading">{{ tooltip.heading }}</div>
          <div class="fiu-cell-tooltip-text">{{ tooltip.body }}</div>
        </div>
      </Teleport>

      <div v-if="!meta.blank && !editorMode" class="flex gap-10px align-center">
        <button class="button" :disabled="submitting || !canUse" @click="canUse ? submit() : $emit('blocked')">
          <LoaderItem size="18px" style="margin-right: 5px" v-if="submitting" />
          Process Data
        </button>
        <button v-if="fileId" class="button" :disabled="downloading" @click="download()">
          <LoaderItem size="18px" style="margin-right: 5px" v-if="downloading" />
          Download
        </button>
        <button v-if="showClear" class="button transparent" :disabled="submitting" @click="clearAll()">Clear</button>
        <span v-if="lookupInProgress" class="flex gap-5px align-center font-size-14"><LoaderItem size="14px" /> Loading player data...</span>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { trimSheetRows } from '@/utils/sheet_utils.js';
import jspreadsheet from 'jspreadsheet-ce';
import jSuites from 'jsuites';
import 'jspreadsheet-ce/dist/jspreadsheet.css';
import 'jsuites/dist/jsuites.css';

import API from '@/api/index.js';
import store from '@/store';
import LoaderItem from '@/components/LoaderItem.vue';
import { PLAYER_KEY_COLUMN, validateValue } from '../../../shared/validation.js';
import { templateValidationRules, gridCellValidationValue, validatorOptions, GRID_DATE_COLUMNS } from '../../../shared/template_fields.js';
import { CALENDAR_FORMAT, DATE_EXAMPLE, formatIsoDate, formatUtcDate, parseDisplayDate } from '@/utils/date_display.js';
import { defaultText, defaultTextColumns } from '../../../shared/default_texts.js';

const props = defineProps({
  // Either a template type (columns are fetched), a ready column definition
  // ({ type, label, isPlayerTemplate, columns }, e.g. a saved sheet's snapshot), or blank.
  templateType: { type: String, default: '' },
  template: { type: Object, default: null },
  blank: { type: Boolean, default: false },
  initialData: { type: Array, default: () => [] },     // rows of cell strings
  initialComments: { type: Object, default: () => ({}) }, // { A1: marker text }
  initialColWidths: { type: Object, default: () => ({}) }, // { <column index>: px } resized by hand
  height: { type: String, default: '60vh' },
  minRows: { type: Number, default: 30 },     // empty rows shown at least
  showLegend: { type: Boolean, default: true },
  showClear: { type: Boolean, default: true },
  // Look up every key already in the data once (fresh imports); fills only empty linked cells
  lookupOnLoad: { type: Boolean, default: false },
  canUse: { type: Boolean, default: true },
  // Editing a template's example rows (admin): no Process/Download/Clear buttons, no rule-based auto-fill
  editorMode: { type: Boolean, default: false },
});
// dirty: content changed (cells, rows, markers); used for autosave
const emit = defineEmits(['blocked', 'dirty']);

// Cell markers live in cell comments: jspreadsheet keeps comments attached to the right cell
// when rows are inserted, deleted or moved, so they double as our per-cell state.
const MARK_AUTO = 'Filled automatically from player data';
const MARK_NOT_FOUND = 'Player not found in imported player data';
const MARK_ERROR = 'Error: ';
const MARK_RULE = 'Filled automatically from the template rules';

const STYLE_AUTO = 'background-color: #e3efff;';
const STYLE_NOT_FOUND = 'background-color: #fff3c4;';
const STYLE_RULE = 'background-color: #e4f4e8;';
const STYLE_ERROR = 'background-color: #f6d3d3; box-shadow: inset 0 0 0 1px #D06A69;';

const LOOKUP_DEBOUNCE_MS = 300;
const LOOKUP_CHUNK = 500;

const gridEl = ref(null);
const meta = ref(null);
const loading = ref(false);
const submitting = ref(false);
const downloading = ref(false);
const lookupInProgress = ref(false);
const fileId = ref(null);
const nonCellErrors = ref([]);
const errorCells = ref([]);   // cell names, in submit order
const errorCursor = ref(-1);

const cellErrorCount = computed(() => errorCells.value.length);

let worksheet = null;
let applyingChanges = false;  // true while we write cells ourselves, so onafterchanges ignores it
let pendingLookupRows = new Set();
let lookupTimer = null;
let headerStyleEl = null;

// Custom tooltip for cell markers. jspreadsheet keeps comments in the cell's `title`
// attribute (getComments reads it back from there), so while a cell is hovered its title
// is parked in data-tip to suppress the native tooltip, and put back afterwards.
const TOOLTIP_MAX_WIDTH = 320;
const tooltip = ref(null); // { heading, body, kind, left, top, below }
let tipCell = null;

function tooltipContent(text) {
  if (text.startsWith(MARK_ERROR)) return { kind: 'error', heading: 'Error', body: text.slice(MARK_ERROR.length) };
  if (text.startsWith(MARK_AUTO)) return { kind: 'auto', heading: '', body: text };
  if (text === MARK_NOT_FOUND) return { kind: 'not-found', heading: '', body: text };
  if (text === MARK_RULE) return { kind: 'rule', heading: '', body: text };
  return { kind: '', heading: '', body: text };
}

function hideTooltip() {
  if (tipCell && !tipCell.hasAttribute('title')) tipCell.setAttribute('title', tipCell.dataset.tip || '');
  if (tipCell) delete tipCell.dataset.tip;
  tipCell = null;
  tooltip.value = null;
}

function showTooltip(td, text) {
  td.dataset.tip = text;
  td.removeAttribute('title');
  tipCell = td;
  const rect = td.getBoundingClientRect();
  const below = rect.top < 80;
  tooltip.value = {
    ...tooltipContent(text),
    below,
    left: Math.max(8, Math.min(rect.left, window.innerWidth - TOOLTIP_MAX_WIDTH - 8)),
    top: below ? rect.bottom + 6 : rect.top - 6,
  };
}

function onGridMouseOver(event) {
  const td = event.target.closest?.('tbody td');
  if (td === tipCell) return;
  hideTooltip();
  const text = td && gridEl.value?.contains(td) ? td.getAttribute('title') : '';
  if (text) showTooltip(td, text);
}

// Comment reads/writes must see the real title of the hovered cell; afterwards hide it again
// (or close the tooltip if the marker was removed).
function withTipTitle(fn) {
  const td = tipCell;
  if (td && !td.hasAttribute('title')) td.setAttribute('title', td.dataset.tip || '');
  try {
    return fn();
  } finally {
    if (td && td === tipCell) {
      const text = td.getAttribute('title') || '';
      if (text) {
        td.removeAttribute('title');
        td.dataset.tip = text;
        tooltip.value = { ...tooltip.value, ...tooltipContent(text) };
      } else {
        hideTooltip();
      }
    }
  }
}

function wrapCommentAccess(ws) {
  const { getComments, setComments } = ws;
  ws.getComments = function (...args) { return withTipTitle(() => getComments.apply(this, args)); };
  ws.setComments = function (...args) { return withTipTitle(() => setComments.apply(this, args)); };
}

const keyColumnIndex = computed(() => meta.value?.columns ? meta.value.columns.findIndex(c => c.isPlayerKey) : -1);
const linkedColumnIndexes = computed(() => meta.value?.columns ? meta.value.columns.map((c, i) => c.isPlayerLinked ? i : -1).filter(i => i >= 0) : []);

// { columnName: { validator_key, skip_null } }: the same rules the server applies on submit
const validationRules = computed(() => meta.value?.columns ? templateValidationRules(meta.value.type || props.templateType) : {});

// Description / Reason columns that have a template default (shared/default_texts.js, the same
// text the server uses when they are left empty)
const defaultTextXs = computed(() => {
  if (!meta.value?.columns || props.editorMode) return [];
  const names = defaultTextColumns(meta.value.type || props.templateType);
  return meta.value.columns.map((c, i) => names.includes(c.name) ? i : -1).filter(i => i >= 0);
});
const defaultTextLabel = computed(() => defaultTextXs.value.map(x => meta.value.columns[x].name).join(' / '));

const cellName = (x, y) => jspreadsheet.helpers.getCellNameFromCoords(x, y);
const getComment = (name) => {
  const comment = worksheet.getComments(name);
  return typeof comment === 'string' ? comment : '';
};
const isBlank = (value) => value === null || value === undefined || value.toString().trim() === '';

// Write cells/styles/comments without re-triggering our change handler.
function silently(fn) {
  applyingChanges = true;
  try { fn(); } finally { applyingChanges = false; }
}

function markCells(cells, style, comment) {
  if (!cells.length) return;
  emit('dirty');
  silently(() => {
    worksheet.resetStyle(Object.fromEntries(cells.map(c => [c, true])), true);
    if (style) worksheet.setStyle(Object.fromEntries(cells.map(c => [c, style])));
    worksheet.setComments(Object.fromEntries(cells.map(c => [c, comment || ''])));
  });
}

function clearMarks(cells) {
  markCells(cells, null, '');
}

function rowCount() {
  return worksheet ? worksheet.options.data.length : 0;
}

// ---------------------------------------------------------------- column widths

// Columns are sized to their content (header and cells) so nothing is cut off.
const MIN_COL_WIDTH = 60;
const MAX_COL_WIDTH = 600;    // keeps one huge value from making a column unusable
const CELL_PADDING = 24;      // cell padding + room for the header's bold/sort styling
const MEASURE_SAMPLE = 30;    // only the longest strings (by length) are measured

let measureCtx = null;

function textWidth(text) {
  if (!measureCtx) {
    measureCtx = document.createElement('canvas').getContext('2d');
    const family = gridEl.value ? getComputedStyle(gridEl.value).fontFamily : 'sans-serif';
    measureCtx.font = `13px ${family}`;
  }
  return measureCtx.measureText(text).width;
}

function fitWidth(header, values, min = MIN_COL_WIDTH) {
  // Character count is a good proxy for width; measuring only the longest keeps big imports fast
  const candidates = values
    .map(v => (v === null || v === undefined) ? '' : v.toString())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .slice(0, MEASURE_SAMPLE);
  candidates.push(header || '');
  const widest = Math.max(...candidates.map(textWidth));
  return Math.round(Math.min(MAX_COL_WIDTH, Math.max(min, widest + CELL_PADDING)));
}

// Resize existing columns without going through setWidth, which would add undo steps.
function refitColumns(xs) {
  if (!worksheet) return;
  const data = worksheet.options.data || [];
  const minWidth = meta.value?.blank ? 110 : MIN_COL_WIDTH;
  for (const x of xs) {
    const col = worksheet.cols?.[x]?.colElement;
    if (!col || worksheet.options.columns?.[x]?.manualWidth) continue;
    const width = fitWidth(worksheet.getHeader(x), data.map(row => row[x]), minWidth);
    if (Number(col.getAttribute('width')) === width) continue;
    col.setAttribute('width', width);
    if (!worksheet.options.columns) worksheet.options.columns = [];
    worksheet.options.columns[x] = { ...(worksheet.options.columns[x] || {}), width };
  }
}

function refitAllColumns() {
  if (worksheet) refitColumns(worksheet.cols.map((_, x) => x));
}

// Width for a new column: the saved manual size, or fitted to the content.
// manualWidth lives on the column options, so the library carries it along when columns move.
function columnWidth(x, fitted) {
  const saved = Number(props.initialColWidths?.[x]);
  return saved > 0 ? { width: saved, manualWidth: true } : { width: fitted() };
}

// A resize by dragging (setWidth); our own auto-fit never goes through setWidth.
function handleResize(instance, column, width) {
  const columns = Array.isArray(column) ? column : [column];
  columns.forEach((x, i) => {
    const px = Array.isArray(width) ? width[i] : width;
    if (!worksheet.options.columns[x]) worksheet.options.columns[x] = {};
    worksheet.options.columns[x].manualWidth = true;
    worksheet.options.columns[x].width = Number(px);
  });
  markDirty();
}

function manualColWidths() {
  const result = {};
  (worksheet.options.columns || []).forEach((col, x) => {
    if (col?.manualWidth && Number(col.width) > 0) result[x] = Math.round(Number(col.width));
  });
  return result;
}

// ---------------------------------------------------------------- setup

// ---------------------------------------------------------------- dropdown columns

// Columns whose validator accepts a fixed list get a searchable dropdown editor.
// jspreadsheet's own 'dropdown' type renders values missing from the list as an empty cell,
// which would hide pasted typos; this editor keeps showing the cell's text as-is
// (live validation marks it red instead).
const listEditor = {
  createCell(cell) {
    cell.classList.add('jss_dropdown');
    return cell;
  },
  setValue(cell, value) {
    cell.textContent = value === null || value === undefined ? '' : value;
  },
  openEditor(cell, value, x, y, instance, options) {
    const current = isBlank(value) ? '' : value.toString();
    const holder = document.createElement('div');
    cell.classList.add('editor');
    cell.innerHTML = '';
    cell.appendChild(holder);
    cell.dataset.originalValue = current;
    jSuites.dropdown(holder, {
      data: options.source.map(option => ({ id: option.value, name: option.label })),
      autocomplete: true,
      opened: true,
      value: options.source.some(option => option.value === current) ? current : '',
      width: '100%',
      position: true, // the grid scrolls (tableOverflow), so the list is positioned fixed
      onclose: () => instance.closeEditor(cell, true),
    });
    // jSuites only picks on Enter/Tab when an item is highlighted, and typing doesn't highlight one:
    // highlight the first match so "type, Enter" picks it (runs before jSuites' document handler)
    const dropdown = holder.dropdown;
    dropdown?.header?.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === 'Tab') && dropdown.currentIndex == null && dropdown.header.value.trim()) {
        dropdown.find(dropdown.header.value);
        dropdown.first();
      }
    });
  },
  closeEditor(cell, save) {
    const dropdown = cell.children[0]?.dropdown;
    const original = cell.dataset.originalValue || '';
    delete cell.dataset.originalValue;
    if (!dropdown) return original;
    const chosen = dropdown.close(true);
    // Closing without picking keeps the old value (Delete clears a cell)
    return save && !isBlank(chosen) ? chosen : original;
  },
};

const isEditing = (td) => td.classList.contains('editor');
const DROPDOWN_ARROW_WIDTH = 24; // clickable area at the right edge of a dropdown cell

// Cells with a button at the right edge: dropdown arrow / date picker calendar icon
const BUTTON_CELL_SELECTOR = 'tbody td.jss_dropdown, tbody td.jss_date';

// Like Excel/Sheets: a click on the cell's arrow opens the list (double-click / typing still edit);
// a click on a date cell's calendar icon opens the date picker.
// Opened after the click finishes so jSuites' outside-click handling doesn't close it right away.
function onGridClick(event) {
  const td = event.target.closest?.(BUTTON_CELL_SELECTOR);
  if (!td || !worksheet || isEditing(td) || td.classList.contains('readonly')) return;
  if (event.clientX < td.getBoundingClientRect().right - DROPDOWN_ARROW_WIDTH) return;
  setTimeout(() => {
    if (!worksheet || isEditing(td)) return;
    openPickerNext = td.classList.contains('jss_date');
    worksheet.openEditor(td, false, event);
    openPickerNext = false;
  });
}

// Darkens the arrow square while the pointer is over it (the part that opens the list on click)
let arrowHotCell = null;
function setArrowHot(td) {
  if (arrowHotCell === td) return;
  arrowHotCell?.classList.remove('arrow-hot');
  arrowHotCell = td;
  td?.classList.add('arrow-hot');
}
function onGridMouseMove(event) {
  const td = event.target.closest?.(BUTTON_CELL_SELECTOR);
  const overArrow = td && !isEditing(td) && event.clientX >= td.getBoundingClientRect().right - DROPDOWN_ARROW_WIDTH;
  setArrowHot(overArrow ? td : null);
}

// Alt+Down opens the list of the selected dropdown cell (before jspreadsheet moves the selection)
function onKeyDown(event) {
  if (!event.altKey || event.key !== 'ArrowDown' || !worksheet || jspreadsheet.current !== worksheet || worksheet.edition) return;
  const selected = worksheet.selectedCell;
  if (!selected) return;
  const td = worksheet.getCellFromCoords(Number(selected[0]), Number(selected[1]));
  const isDate = td?.classList.contains('jss_date');
  if (!td || td.classList.contains('readonly') || (!isDate && !td.classList.contains('jss_dropdown'))) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  openPickerNext = isDate;
  worksheet.openEditor(td, false, event);
  openPickerNext = false;
}

// ---------------------------------------------------------------- date cells

// Date cells keep ISO text ("2019-08-31") - what the server and validation expect - but are shown,
// edited, typed and pasted in the browser's locale format (08/31/2019 in the US, 31/08/2019 in
// the UK), the way Excel shows date cells. In plain sheets every cell holding a full date does this.
const dateEditor = {
  createCell(cell, value) {
    cell.textContent = formatIsoDate(value);
    return cell;
  },
  setValue(cell, value) {
    cell.textContent = formatIsoDate(value);
  },
  // Text in the locale format (or ISO) is stored as ISO; anything else is kept as typed
  // (live validation marks it in date columns)
  updateCell(cell, value) {
    if (isBlank(value)) return value;
    return parseDisplayDate(value, { allowTime: !!meta.value?.blank }) ?? value;
  },
  openEditor(cell, value, x, y, instance, options, event) {
    // Typing on a selected cell replaces its content, like the default editor (the key lands in the input)
    const typing = event?.type === 'keydown' && event.key?.length === 1 && !event.ctrlKey && !event.metaKey;
    const shown = typing ? '' : formatIsoDate(value);
    const rect = cell.getBoundingClientRect();
    const input = document.createElement('input');
    input.style.width = `${rect.width}px`;
    input.style.height = `${rect.height - 2}px`;
    input.style.minHeight = `${rect.height - 2}px`;
    cell.classList.add('editor');
    cell.innerHTML = '';
    cell.appendChild(input);
    cell.dataset.originalValue = isBlank(value) ? '' : value.toString();
    if (!typing) cell.dataset.shownValue = shown;
    input.value = shown;
    input.focus();
    input.onblur = () => { if (instance.edition) instance.closeEditor(cell, true); };
  },
  closeEditor(cell, save) {
    const input = cell.querySelector('input');
    const original = cell.dataset.originalValue ?? '';
    const shown = cell.dataset.shownValue;
    delete cell.dataset.originalValue;
    delete cell.dataset.shownValue;
    if (input) input.onblur = null;
    if (!input || !save) return original;
    // Left as shown: keep the stored text, so opening and closing a cell isn't an edit
    return input.value === shown ? original : input.value;
  },
};

// Template date columns: same as above, plus a calendar picker opened from the cell's calendar icon
// (or Alt+Down). Double-click / typing still edits the text, so dates can be typed or pasted.
let openPickerNext = false;
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}/;

const datePickerEditor = {
  ...dateEditor,
  createCell(cell, value) {
    cell.classList.add('jss_date');
    return dateEditor.createCell(cell, value);
  },
  openEditor(cell, value, x, y, instance, options, event) {
    if (!openPickerNext) return dateEditor.openEditor(cell, value, x, y, instance, options, event);
    openPickerNext = false;
    const original = isBlank(value) ? '' : value.toString();
    const iso = ISO_DAY_RE.test(original) && parseDisplayDate(original.slice(0, 10)) ? original.slice(0, 10) : null;
    const rect = cell.getBoundingClientRect();
    const input = document.createElement('input');
    input.style.width = `${rect.width}px`;
    input.style.height = `${rect.height - 2}px`;
    input.style.minHeight = `${rect.height - 2}px`;
    cell.classList.add('editor');
    cell.innerHTML = '';
    cell.appendChild(input);
    cell.dataset.originalValue = original;
    cell.dataset.pickerValue = iso || '';
    jSuites.calendar(input, {
      format: CALENDAR_FORMAT,
      value: iso,
      opened: true,
      readonly: true,
      position: true, // the grid scrolls (tableOverflow), so the calendar is positioned fixed
      onclose: () => { if (instance.edition) instance.closeEditor(cell, true); },
    });
  },
  closeEditor(cell, save) {
    const calendar = cell.querySelector('input')?.calendar;
    if (!calendar) return dateEditor.closeEditor(cell, save);
    const original = cell.dataset.originalValue ?? '';
    const initial = cell.dataset.pickerValue;
    delete cell.dataset.originalValue;
    delete cell.dataset.pickerValue;
    const chosen = (calendar.close(true) || '').toString();
    if (!save) return original;
    // "Reset" in the calendar clears the cell; closing without picking keeps the stored text
    if (chosen === '' && initial) return '';
    const day = ISO_DAY_RE.exec(chosen)?.[0];
    return day && day !== initial ? day : original;
  },
};

const isDateColumnName = (name) => GRID_DATE_COLUMNS.includes(name);

// Plain sheets: columns added later (spare columns, inserts) get the date handling too.
// Runs before jspreadsheet looks at the column type for the change.
function ensureBlankColumnType(instance, x) {
  if (!meta.value?.blank) return;
  const columns = instance.options.columns || (instance.options.columns = []);
  if (!columns[x]) columns[x] = {};
  if (!columns[x].type) columns[x].type = dateEditor;
}

function columnType(col) {
  if (isDateColumnName(col.name)) return { type: datePickerEditor };
  const rule = validationRules.value[col.name];
  const options = rule && validatorOptions(rule.validator_key);
  return options?.length ? { type: listEditor, source: options } : { type: 'text' };
}

// Empty columns shown after the last template column (ignored on submit)
const EXTRA_TEMPLATE_COLS = 1;

function buildNestedHeaders(columns) {
  if (!columns.some(c => c.group)) return undefined;
  const row = [];
  for (const col of columns) {
    const title = col.group || '';
    const last = row[row.length - 1];
    if (last && last.title === title) last.colspan += 1;
    else row.push({ title, colspan: 1 });
  }
  // Cover the trailing free column too
  for (let i = 0; i < EXTRA_TEMPLATE_COLS; i++) row.push({ title: '', colspan: 1 });
  return [row];
}

function applyHeaderStyles(columns) {
  // Header cells are rendered by the library; colour key/linked columns with a generated stylesheet.
  headerStyleEl?.remove();
  headerStyleEl = document.createElement('style');
  const rules = [];
  columns.forEach((col, x) => {
    const selector = `.fiu-grid .jss_worksheet thead tr:last-child td[data-x="${x}"]`;
    if (col.isPlayerKey) rules.push(`${selector} { background: #ffd98a !important; font-weight: 700; }`);
    else if (col.isPlayerLinked) rules.push(`${selector} { background: #cfe2ff !important; }`);
  });
  headerStyleEl.textContent = rules.join('\n');
  document.head.appendChild(headerStyleEl);
}

function destroyGrid() {
  hideTooltip();
  if (worksheet && gridEl.value) {
    jspreadsheet.destroy(gridEl.value, true);
  }
  worksheet = null;
  clearTimeout(lookupTimer);
  pendingLookupRows = new Set();
}

const markDirty = () => emit('dirty');

function initialRows(width) {
  // Copy: the grid mutates the array it gets
  return (props.initialData || []).map(row => {
    const cells = (row || []).map(value => (value === null || value === undefined) ? '' : value.toString());
    while (cells.length < width) cells.push('');
    return cells;
  });
}

function worksheetOptions(columns) {
  if (!columns) {
    // Blank sheet: lettered columns, everything editable
    const width = Math.max(26, ...(props.initialData || []).map(row => (row || []).length));
    const data = initialRows(width);
    return {
      data,
      columns: Array.from({ length: width }, (_, x) => ({
        type: dateEditor,
        ...columnWidth(x, () => fitWidth(jspreadsheet.helpers.getColumnName(x), data.map(row => row[x]), 110)),
      })),
      minDimensions: [width, Math.max(props.minRows, 100, data.length + 20)],
      minSpareRows: 3,
      // Always keep one empty column after the last filled one
      minSpareCols: 1,
      defaultColWidth: 110,
      defaultColAlign: 'left',
      tableOverflow: true,
      tableWidth: '100%',
      tableHeight: props.height,
      allowComments: false,
    };
  }
  const width = columns.length + EXTRA_TEMPLATE_COLS;
  const data = initialRows(width).map(row => row.slice(0, width));
  return {
    data,
    minDimensions: [width, Math.max(props.minRows, data.length + 20)],
    minSpareRows: 3,
    columns: columns.map((col, x) => ({
      title: col.name,
      ...columnType(col),
      align: 'left',
      ...columnWidth(x, () => fitWidth(col.name, data.map(row => row[x]))),
    })).concat(Array.from({ length: EXTRA_TEMPLATE_COLS }, (_, i) => ({
      // Free column after the template ones; not sent on submit
      title: ' ',
      type: 'text',
      align: 'left',
      ...columnWidth(columns.length + i, () => fitWidth('', data.map(row => row[columns.length + i]), 110)),
    }))),
    nestedHeaders: buildNestedHeaders(columns),
    tableOverflow: true,
    tableWidth: '100%',
    tableHeight: props.height,
    allowInsertColumn: false,
    allowManualInsertColumn: false,
    allowDeleteColumn: false,
    allowRenameColumn: false,
    columnDrag: false,
    columnSorting: false,
    allowComments: false,
  };
}

// Saved markers come back as comments; give them their colour again
function restoreMarkers() {
  const comments = Object.entries(props.initialComments || {})
    .filter(([, text]) => typeof text === 'string' && !text.startsWith(MARK_ERROR));
  if (!comments.length) return;
  silently(() => {
    worksheet.setComments(Object.fromEntries(comments));
    const styles = {};
    for (const [name, text] of comments) {
      if (text.startsWith(MARK_AUTO)) styles[name] = STYLE_AUTO;
      else if (text === MARK_NOT_FOUND) styles[name] = STYLE_NOT_FOUND;
      else if (text === MARK_RULE) styles[name] = STYLE_RULE;
    }
    if (Object.keys(styles).length) worksheet.setStyle(styles);
  });
}

async function loadTemplate() {
  destroyGrid();
  resetResult();
  meta.value = null;
  let response;
  if (props.blank) {
    response = { blank: true, columns: null, isPlayerTemplate: false };
  } else if (props.template) {
    response = props.template;
  } else {
    if (!props.templateType) return;
    loading.value = true;
    response = await API.getTemplateColumns(props.templateType);
    loading.value = false;
    if (!response) return;
  }
  meta.value = response;
  await nextTick();

  [worksheet] = jspreadsheet(gridEl.value, {
    about: false,
    allowExport: false,
    worksheets: [worksheetOptions(response.columns)],
    onbeforechange: (instance, cell, x) => { ensureBlankColumnType(instance, Number(x)); },
    onafterchanges: (instance, changes) => handleChanges(changes),
    oninsertrow: markDirty,
    ondeleterow: markDirty,
    onmoverow: markDirty,
    oninsertcolumn: markDirty,
    ondeletecolumn: markDirty,
    onmovecolumn: markDirty,
    onsort: markDirty,
    onresizecolumn: handleResize,
    onundo: () => { markDirty(); refitAllColumns(); },
    onredo: () => { markDirty(); refitAllColumns(); },
  });
  wrapCommentAccess(worksheet);
  restoreMarkers();
  // Saved sheets don't keep error markers: re-check what's already there
  if (response.columns) validateAllCells();
  if (response.columns) applyHeaderStyles(response.columns);
  fillDefaultTexts([...Array(rowCount()).keys()]);
  if (props.lookupOnLoad) lookupAllKeys();
}

function lookupAllKeys() {
  const keyX = keyColumnIndex.value;
  if (keyX < 0 || !linkedColumnIndexes.value.length) return;
  for (let y = 0; y < rowCount(); y++) {
    if (!isBlank(worksheet.getValueFromCoords(keyX, y))) pendingLookupRows.add(y);
  }
  if (pendingLookupRows.size) runLookups();
}

// Current content for saving: rows of strings without the empty padding rows, plus markers
// (errors are transient, not saved)
function getSnapshot() {
  if (!worksheet) return null;
  const data = trimSheetRows(worksheet.getData());
  const all = worksheet.getComments();
  const comments = {};
  if (all && typeof all === 'object') {
    for (const [name, text] of Object.entries(all)) {
      if (typeof text === 'string' && text && !text.startsWith(MARK_ERROR)) comments[name] = text;
    }
  }
  return { data, comments, colWidths: manualColWidths() };
}

defineExpose({ getSnapshot });

// ---------------------------------------------------------------- change handling

function handleChanges(changes) {
  if (!worksheet) return;
  emit('dirty');
  // Also runs for our own writes (player lookups fill cells too)
  refitColumns([...new Set(changes.map(c => Number(c.x)))]);
  if (applyingChanges) return;
  resetResult(false);

  if (meta.value?.blank) return;
  const keyX = keyColumnIndex.value;
  const linked = new Set(linkedColumnIndexes.value);
  const toUnmark = [];

  for (const change of changes) {
    const x = Number(change.x);
    const y = Number(change.y);
    const name = cellName(x, y);
    const comment = getComment(name);

    // Any edit of a flagged cell removes the flag (manual override / error fixed)
    if (comment && (comment.startsWith(MARK_AUTO) || comment.startsWith(MARK_ERROR) || comment === MARK_NOT_FOUND || comment === MARK_RULE)) {
      toUnmark.push(name);
    }
    if (x === keyX && linked.size) {
      pendingLookupRows.add(y);
    }
  }
  clearMarks(toUnmark);
  errorCells.value = errorCells.value.filter(name => !toUnmark.includes(name));
  // Cells the user just edited keep what they typed (even if they cleared them)
  fillDefaultTexts([...new Set(changes.map(c => Number(c.y)))], new Set(changes.map(c => cellName(Number(c.x), Number(c.y)))));
  // jspreadsheet commits an edit when the cell loses focus (and on paste), so this runs on focus-out
  validateCells(changes.map(c => ({ x: Number(c.x), y: Number(c.y) })));

  if (pendingLookupRows.size) {
    clearTimeout(lookupTimer);
    lookupTimer = setTimeout(runLookups, LOOKUP_DEBOUNCE_MS);
  }
}

// ---------------------------------------------------------------- template default texts

// Fills empty Description / Reason cells of the given rows with the template default, for rows
// that have data and everything the text depends on (Transfer Type, Funds Type, PSP Name...).
// Cells filled this way are marked; they follow later changes of the row (or are emptied again
// when the text can no longer be derived), until the user edits them.
function fillDefaultTexts(rows, skipCells = new Set()) {
  const xs = defaultTextXs.value;
  const columns = meta.value?.columns;
  if (!worksheet || !columns || !xs.length || !rows.length) return;
  const type = meta.value.type || props.templateType;
  const writes = [];
  const toMark = [];
  const toUnmark = [];
  for (const y of rows) {
    const row = {};
    let hasData = false;
    columns.forEach((col, x) => {
      const value = worksheet.getValueFromCoords(x, y);
      row[col.name] = isBlank(value) ? '' : value.toString().trim();
      if (row[col.name] && !xs.includes(x)) hasData = true;
    });
    for (const x of xs) {
      const name = cellName(x, y);
      if (skipCells.has(name)) continue;
      const current = row[columns[x].name];
      const isRuleFilled = getComment(name) === MARK_RULE;
      if (current && !isRuleFilled) continue; // the user's own text wins
      const value = hasData ? defaultText(type, columns[x].name, row) : null;
      if (value) {
        if (isRuleFilled && current === value) continue;
        writes.push({ x, y, value });
        toMark.push(name);
      } else if (isRuleFilled) {
        writes.push({ x, y, value: '' });
        toUnmark.push(name);
      }
    }
  }
  if (writes.length) silently(() => worksheet.setValue(writes));
  markCells(toMark, STYLE_RULE, MARK_RULE);
  clearMarks(toUnmark);
}

// ---------------------------------------------------------------- live validation

// Row values by column name, typed the way the server sees them after submit
function rowValues(y) {
  const row = {};
  meta.value.columns.forEach((col, x) => {
    const value = gridCellValidationValue(col.name, worksheet.getValueFromCoords(x, y));
    if (value !== null) row[col.name] = value;
  });
  return row;
}

// Marks invalid cells red with the error as its tooltip. Empty cells are skipped
// ("missing value" is reported on submit), and so are cells carrying another marker
// (auto-filled / player not found) so those markers aren't lost.
function validateCells(cells) {
  const rules = validationRules.value;
  const columns = meta.value?.columns;
  if (!worksheet || !columns || !Object.keys(rules).length) return;
  const rowCache = {};
  const errors = {}; // cell name -> error text
  for (const { x, y } of cells) {
    const rule = columns[x] && rules[columns[x].name];
    if (!rule) continue;
    const value = gridCellValidationValue(columns[x].name, worksheet.getValueFromCoords(x, y));
    if (value === null) continue;
    const name = cellName(x, y);
    if (getComment(name)) continue;
    const found = [];
    try {
      validateValue({
        key: rule.validator_key,
        name: columns[x].name,
        value,
        errorList: found,
        index: y,
        skipp_null: rule.skip_null,
        row: rowCache[y] ||= rowValues(y),
      });
    } catch (error) {
      console.error('Cell validation failed', name, error);
      continue;
    }
    if (!found.length) continue;
    // The server's message asks for YYYY-MM-DD; in the grid dates are typed in the locale format
    if (isDateColumnName(columns[x].name) && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      errors[name] = `${MARK_ERROR}"${value}" is not a valid date, use the format ${DATE_EXAMPLE}`;
      continue;
    }
    errors[name] = `${MARK_ERROR}${found.map(e => e.errorText).join('\n')}`;
  }
  const names = Object.keys(errors);
  if (!names.length) return;
  silently(() => {
    worksheet.setStyle(Object.fromEntries(names.map(name => [name, STYLE_ERROR])));
    worksheet.setComments(errors);
  });
  errorCells.value = [...errorCells.value, ...names.filter(name => !errorCells.value.includes(name))];
}

function validateAllCells() {
  const columns = meta.value?.columns;
  if (!worksheet || !columns) return;
  const rules = validationRules.value;
  const xs = columns.map((col, x) => rules[col.name] ? x : -1).filter(x => x >= 0);
  const cells = [];
  for (let y = 0; y < rowCount(); y++) {
    for (const x of xs) cells.push({ x, y });
  }
  validateCells(cells);
}

async function runLookups() {
  if (!worksheet) return;
  const rows = [...pendingLookupRows];
  pendingLookupRows = new Set();
  const keyX = keyColumnIndex.value;

  // Changing the key replaces whatever the previous key filled in; manual values are kept.
  const keyByRow = {};
  const staleAuto = [];
  for (const y of rows) {
    const key = worksheet.getValueFromCoords(keyX, y);
    keyByRow[y] = isBlank(key) ? null : key.toString().trim();
    for (const x of linkedColumnIndexes.value) {
      const name = cellName(x, y);
      if (getComment(name).startsWith(MARK_AUTO)) staleAuto.push({ x, y, name });
    }
  }
  if (staleAuto.length) {
    silently(() => worksheet.setValue(staleAuto.map(({ x, y }) => ({ x, y, value: '' }))));
    clearMarks(staleAuto.map(c => c.name));
  }

  const ids = [...new Set(Object.values(keyByRow).filter(Boolean))];
  if (!ids.length) return;

  lookupInProgress.value = true;
  const players = {};
  for (let i = 0; i < ids.length; i += LOOKUP_CHUNK) {
    const result = await API.lookupPlayers(ids.slice(i, i + LOOKUP_CHUNK));
    if (result === false) { lookupInProgress.value = false; return; }
    Object.assign(players, result);
  }
  lookupInProgress.value = false;
  if (!worksheet) return;

  const columns = meta.value.columns;
  const writes = [];
  const autoCells = {};       // comment text -> [cell names]
  const notFoundCells = [];
  for (const y of rows) {
    const key = keyByRow[y];
    if (!key) continue;
    // The user may have typed another key while we were waiting
    const current = worksheet.getValueFromCoords(keyX, y);
    if (isBlank(current) || current.toString().trim() !== key) continue;

    const player = players[key];
    if (!player) {
      notFoundCells.push(cellName(keyX, y));
      continue;
    }
    const comment = `${MARK_AUTO}${player.importDate ? ` (import of ${formatUtcDate(player.importDate)})` : ''}`;
    for (const x of linkedColumnIndexes.value) {
      const value = player.values[columns[x].name];
      if (isBlank(value) || !isBlank(worksheet.getValueFromCoords(x, y))) continue;
      writes.push({ x, y, value });
      (autoCells[comment] ||= []).push(cellName(x, y));
    }
  }
  if (writes.length) silently(() => worksheet.setValue(writes));
  for (const [comment, cells] of Object.entries(autoCells)) markCells(cells, STYLE_AUTO, comment);
  markCells(notFoundCells, STYLE_NOT_FOUND, MARK_NOT_FOUND);
  // Player data may have filled a value the default texts depend on
  fillDefaultTexts(rows);
}

// ---------------------------------------------------------------- submit / results

function resetResult(clearCellMarks = true) {
  fileId.value = null;
  nonCellErrors.value = [];
  if (clearCellMarks && worksheet && errorCells.value.length) {
    clearMarks(errorCells.value.filter(name => getComment(name).startsWith(MARK_ERROR)));
  }
  if (clearCellMarks) {
    errorCells.value = [];
    errorCursor.value = -1;
  }
}

// Rows with at least one value; the server skips empty rows the same way,
// so error.index (position among sent rows) maps back through this list.
function collectRows() {
  const columns = meta.value.columns;
  const rows = [];
  const gridRowBySentIndex = [];
  for (let y = 0; y < rowCount(); y++) {
    const row = {};
    let hasData = false;
    columns.forEach((col, x) => {
      const value = worksheet.getValueFromCoords(x, y);
      if (!isBlank(value)) {
        row[col.name] = value.toString().trim();
        hasData = true;
      }
    });
    if (hasData) {
      rows.push(row);
      gridRowBySentIndex.push(y);
    }
  }
  return { rows, gridRowBySentIndex };
}

async function submit() {
  if (!worksheet) return;
  resetResult();
  const { rows, gridRowBySentIndex } = collectRows();
  if (!rows.length) {
    store.dispatch('addNotification', { text: 'The grid is empty', type: 'error' });
    return;
  }

  submitting.value = true;
  const response = await API.submitGrid({ type: meta.value.type || props.templateType, filename: meta.value.label, rows });
  submitting.value = false;
  if (!response) return;

  const errors = response.found_errors || [];
  if (response.status_code > 299 || errors.length) {
    showErrors(errors, gridRowBySentIndex);
    if (response.status_code > 299 && response.message) {
      store.dispatch('addNotification', { text: response.message, type: 'error' });
    }
  } else {
    fileId.value = response.id;
    store.dispatch('addNotification', { text: `${rows.length} row${rows.length > 1 ? 's' : ''} processed successfully`, type: 'success' });
  }
  for (const warning of (response.warnings || []).slice(0, 1)) {
    store.dispatch('addNotification', { text: warning, type: 'warning' });
  }
}

function showErrors(errors, gridRowBySentIndex) {
  const columnIndex = Object.fromEntries(meta.value.columns.map((c, i) => [c.name, i]));
  const byCell = {};
  const other = [];
  for (const error of errors) {
    const x = columnIndex[error.columnName];
    const y = gridRowBySentIndex[error.index];
    if (x === undefined || y === undefined) {
      other.push({ ...error, gridRow: y === undefined ? null : y + 1 });
      continue;
    }
    const name = cellName(x, y);
    (byCell[name] ||= []).push(error.errorText);
  }
  for (const [name, texts] of Object.entries(byCell)) {
    markCells([name], STYLE_ERROR, `${MARK_ERROR}${texts.join('\n')}`);
  }
  nonCellErrors.value = other;
  errorCells.value = Object.keys(byCell);
  if (errorCells.value.length) focusError(1);
}

// Errors that can't be placed on a cell (column not in the grid, or file-level errors)
function describeError(error) {
  const where = [
    error.gridRow ? `Row ${error.gridRow}` : null,
    error.columnName ? `column "${error.columnName}"` : null,
  ].filter(Boolean).join(', ');
  return where ? `${where}: ${error.errorText}` : error.errorText;
}

function focusError(step) {
  if (!errorCells.value.length) return;
  const count = errorCells.value.length;
  errorCursor.value = (errorCursor.value + step + count) % count;
  const [x, y] = jspreadsheet.helpers.getCoordsFromCellName(errorCells.value[errorCursor.value]);
  worksheet.updateSelectionFromCoords(x, y, x, y);
  worksheet.getCellFromCoords(x, y)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
}

async function download() {
  downloading.value = true;
  await API.downloadFile(fileId.value, { name: `${meta.value.label || meta.value.type || props.templateType}.xlsx` });
  downloading.value = false;
}

function clearAll() {
  if (!worksheet) return;
  emit('dirty');
  resetResult();
  silently(() => {
    worksheet.setData([]);
    worksheet.resetStyle(Object.fromEntries(Object.keys(worksheet.getStyle() || {}).map(c => [c, true])), true);
    const comments = worksheet.getComments();
    if (comments && typeof comments === 'object') {
      worksheet.setComments(Object.fromEntries(Object.keys(comments).map(c => [c, ''])));
    }
  });
  refitAllColumns();
}

watch([() => props.templateType, () => props.template, () => props.blank], loadTemplate);
onMounted(() => {
  // Capture phase: runs before jspreadsheet's own document keydown handler
  document.addEventListener('keydown', onKeyDown, true);
  loadTemplate();
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeyDown, true);
  destroyGrid();
  headerStyleEl?.remove();
});
</script>

<style scoped lang="scss">
.fiu-grid {
  text-align: left;
}
.grid-holder {
  overflow: hidden;
  :deep(.jss_worksheet) {
    font-size: 13px;
  }
}
.legend {
  flex-wrap: wrap;
}
.swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid #bbb;
  &.key { background: #ffd98a; }
  &.linked { background: #e3efff; }
  &.not-found { background: #fff3c4; }
  &.rule { background: #e4f4e8; }
  &.error { background: #f6d3d3; border-color: #D06A69; }
}
.linked-text {
  background: #cfe2ff;
  padding: 0 4px;
  border-radius: 3px;
}
.hint {
  color: #555;
}
.dropdown-text {
  color: #999;
}
/* jspreadsheet marks selected cells with .highlight, which also matches App.vue's global
   flash-highlight animation (used elsewhere for "scroll to this"): that animation flashed every
   selected cell blue and, animating the background shorthand, hid the dropdown arrow. */
.grid-holder :deep(tbody td.highlight) {
  animation: none;
  background-color: rgba(0, 0, 0, .05);
}
/* Dropdown cells (Excel/Sheets-style): the arrow, in a small button-like square, shows only on the
   hovered / selected cell; it darkens when the pointer is over it (.arrow-hot, set in onGridMouseMove)
   and stays in the same spot, highlighted, while the list is open. */
.grid-holder :deep(td.jss_dropdown) {
  padding-right: 26px;
  background-image: none;
}
.grid-holder :deep(td.jss_dropdown:not(.editor):hover),
.grid-holder :deep(td.jss_dropdown.highlight-selected:not(.editor)) {
  background-repeat: no-repeat;
  background-position: right 3px center;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Crect x='0.5' y='0.5' width='19' height='19' rx='4' fill='%23f6f7f8' stroke='%23dfe1e5'/%3E%3Cpath d='M6 8.5l4 4 4-4z' fill='%235f6368'/%3E%3C/svg%3E");
}
.grid-holder :deep(td.jss_dropdown.arrow-hot:not(.editor)) {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Crect x='0.5' y='0.5' width='19' height='19' rx='4' fill='%23e8eaed' stroke='%23bdc1c6'/%3E%3Cpath d='M6 8.5l4 4 4-4z' fill='%23202124'/%3E%3C/svg%3E");
}
/* Date cells: a calendar icon in the same kind of square opens the date picker */
.grid-holder :deep(td.jss_date:not(.editor)) {
  padding-right: 26px;
}
.grid-holder :deep(td.jss_date:not(.editor):hover),
.grid-holder :deep(td.jss_date.highlight-selected:not(.editor)) {
  background-repeat: no-repeat;
  background-position: right 3px center;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Crect x='0.5' y='0.5' width='19' height='19' rx='4' fill='%23f6f7f8' stroke='%23dfe1e5'/%3E%3Crect x='5' y='6' width='10' height='9' rx='1' fill='none' stroke='%235f6368' stroke-width='1.3'/%3E%3Cpath d='M5 9h10M8 4.5v3M12 4.5v3' stroke='%235f6368' stroke-width='1.3'/%3E%3C/svg%3E");
}
.grid-holder :deep(td.jss_date.arrow-hot:not(.editor)) {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Crect x='0.5' y='0.5' width='19' height='19' rx='4' fill='%23e8eaed' stroke='%23bdc1c6'/%3E%3Crect x='5' y='6' width='10' height='9' rx='1' fill='none' stroke='%23202124' stroke-width='1.3'/%3E%3Cpath d='M5 9h10M8 4.5v3M12 4.5v3' stroke='%23202124' stroke-width='1.3'/%3E%3C/svg%3E");
}
/* While editing, jSuites draws the arrow on its search box: span the whole cell, same square, same spot */
.grid-holder :deep(td.jss_dropdown.editor) {
  padding-right: 0;
}
.grid-holder :deep(td.jss_dropdown.editor .jdropdown-header) {
  padding-right: 28px;
  background-position: right 2px center; /* the search box ends 1px short of the cell edge */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Crect x='0.5' y='0.5' width='19' height='19' rx='4' fill='%23e8f0fe' stroke='%238ab4f8'/%3E%3Cpath d='M6 8.5l4 4 4-4z' fill='%231a73e8'/%3E%3C/svg%3E");
}
.errors-box {
  background: #fbeaea;
  border: 1px solid #D06A69;
  border-radius: 5px;
  padding: 10px;
}
.toggle-error {
  background-color: #D06A69;
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  .button.small {
    width: auto;
    flex: none;
    padding: 4px 10px;
    font-size: 13px;
    color: white;
    background: transparent;
    border: 1px solid white;
    &:hover { background: rgba(255, 255, 255, .15); }
  }
}
</style>

<style lang="scss">
/* Teleported to <body>, so not scoped */
.fiu-cell-tooltip {
  position: fixed;
  z-index: 10000;
  max-width: 320px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #2b2f36;
  color: #fff;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  box-shadow: 0 4px 14px rgba(0, 0, 0, .18);
  pointer-events: none;
  transform: translateY(-100%);
  &.below { transform: none; }
  &::after {
    content: '';
    position: absolute;
    left: 14px;
    top: 100%;
    border: 6px solid transparent;
    border-top-color: var(--tip-bg, #2b2f36);
  }
  &.below::after {
    top: auto;
    bottom: 100%;
    border-top-color: transparent;
    border-bottom-color: var(--tip-bg, #2b2f36);
  }
  &.error {
    --tip-bg: #D06A69;
    background: #fff;
    color: #3a1d1d;
    border: 1px solid #D06A69;
    border-left: 4px solid #D06A69;
    &::after { left: 11px; }
  }
  &.auto { --tip-bg: #1f4f99; background: #1f4f99; }
  &.rule { --tip-bg: #2e6b3f; background: #2e6b3f; }
  &.not-found { --tip-bg: #7a5b00; background: #7a5b00; }
}
.fiu-cell-tooltip-heading {
  font-weight: 600;
  color: #c5221f;
  margin-bottom: 2px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .03em;
}
.fiu-cell-tooltip-text {
  white-space: pre-line;
  word-break: break-word;
}
</style>
